import React from 'react';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import _isBoolean from 'lodash/isBoolean';
import _isEmpty from 'lodash/isEmpty';
import _isPlainObject from 'lodash/isPlainObject';
import _isFunction from 'lodash/isFunction';

import { EMAIL_REGEX } from 'constants/logic';
import { isRegexMatch } from 'helpers/core';
import { isMessage } from 'helpers/intl-fns';

import messages from './messages';

/*
  Accecpts the same shapes as `react-hook-form : register` but with messages
  instead of strings (and an additional `values` field) and formats's them into
  the strings that `react-hook-form : register` wants.

  `validate`, normally, only errors when a string is returned so, to support
  messages, we need to adopt the `{ value, message }` shape (which RHF) doesn't
  support normally.

  required: true
  required: messages.required
  required: {
    value: true,
    message: messages.required
  }
    --> required: { value: true, message: 'This field is required' }

  maxLength: 200
  maxLength: {
    value: 200,
    message: messages.maxLength, // "The text, "{example}", must be less than {maxLength, number} characters"
    values: { example: 'foo' }, // maxValue will be automatically added
  }
    --> maxLength: { value: 200, message: 'The text, "foo", must be less than 200 characters' }

  validate: {
    value: value => value === "legit",
    message: messages.notLegitEnough,
  }
*/
export function makeValidators(props, intl) {
  const { required, maxLength, minLength, max, min, pattern, validate, type } = props;
  let validators = {};

  if (required) {
    switch (true) {
      case _isBoolean(required):
        validators.required = {
          value: true,
          message: messages.error.required,
        };
        break;

      case isMessage(required):
        validators.required = {
          value: true,
          message: required,
        };
        break;

      case _isPlainObject(required):
        validators.required = required;
        break;

      default:
        break;
    }
  }

  if (maxLength) {
    validators.maxLength = !_isPlainObject(maxLength)
      ? {
          value: maxLength,
          message: messages.error.maxLength,
          values: { maxLength },
        }
      : {
          ...maxLength,
          values: {
            ...maxLength.values,
            maxLength,
          },
        };
  }

  if (minLength) {
    validators.minLength = !_isPlainObject(minLength)
      ? {
          value: minLength,
          message: messages.error.minLength,
          values: { minLength },
        }
      : {
          ...minLength,
          values: {
            ...minLength.values,
            minLength,
          },
        };
  }

  if (max) {
    validators.max = !_isPlainObject(max)
      ? {
          value: max,
          message: messages.error.max,
          values: { max },
        }
      : {
          ...max,
          values: {
            ...max.values,
            max,
          },
        };
  }

  if (min) {
    validators.min = !_isPlainObject(min)
      ? {
          value: min,
          message: messages.error.min,
          values: { min },
        }
      : {
          ...min,
          values: {
            ...min.values,
            min,
          },
        };
  }

  if (pattern) {
    validators.pattern = _isPlainObject(pattern)
      ? pattern
      : {
          value: pattern,
          message: messages.error.generic,
        };
  }

  if (validate) {
    validators.validate = value => {
      const validatedValue = validate.value(value);
      if (validatedValue) return validatedValue;
      const message = _isFunction(validate.message)
        ? validate.message({ ...validate.values, value })
        : validate.message;
      const errorText = intl.formatMessage(message, { ...validate.values, value });
      // RHF ignores errors unless it's a string or a valid react element
      // If intl.formatMessage contains rich text the response will be an array.
      // So we wrap it in a fragment to force RHF to honor the error.
      return React.createElement(React.Fragment, {}, errorText);
    };
  } else if (type === 'tel') {
    validators.validate = value => {
      if (!value) return true;
      return isPossiblePhoneNumber(value) || intl.formatMessage(messages.error.phone);
    };
  } else if (type === 'email') {
    validators.validate = value => {
      if (!value) return true;
      return isRegexMatch(value, EMAIL_REGEX) || intl.formatMessage(messages.error.email);
    };
  }

  validators = Object.entries(validators).reduce((acc, [k, v]) => {
    if (!_isPlainObject(v)) {
      return {
        ...acc,
        [k]: v,
      };
    }

    const { value, message, values = {} } = v;
    return {
      ...acc,
      [k]: {
        value,
        message: intl.formatMessage(message, values),
      },
    };
  }, {});

  return validators;
}

/*
  Handles all of our async form binding and passes the resulting async-control functions
  along with the form values.

    // in the saga
    exampleAction = (formValues, { resolveSubmit, rejectSubmit }) => ...

    // in the container
    mapDispatchToProps = { exampleSubmit: exampleAction }

    // in the component
    <Form onSubmit={exampleSubmit} />

  Calling `resolveSubmit` with a message (and optional values obj) will show an info banner.

    yield call(resolveSubmit, message, values)

  Calling `rejectSubmit` with a message (and optional values obj) will show an error banner.

    yield call(rejectSubmit, message, values)

  Alternately, calling `rejectSubmit` without a message but with an object mapping errors
  to field-names.  The error will appear on the corresponding field exactly like an error
  defined on the field itself (i.e. when calling `register`).

    yield call(resolveSubmit, null, { email: '-- custom error --' })
*/
export const asyncOnSubmit =
  (onSubmit, createErrorBanner, createInfoBanner) =>
  ({ setError, reset }) =>
  formValues =>
    new Promise(resolve => {
      onSubmit(formValues, {
        resolveSubmit: (message, values = {}) => {
          resolve();
          reset(formValues);
          if (message && createInfoBanner) {
            createInfoBanner(message, values);
          }
        },
        rejectSubmit: (message, messageValuesOrValidationErrors = {}) => {
          resolve();

          if (createErrorBanner && message) {
            createErrorBanner(message, messageValuesOrValidationErrors);
          }

          if (setError && !message && !_isEmpty(messageValuesOrValidationErrors)) {
            const formattedErrors = Object.entries(messageValuesOrValidationErrors).reduce(
              (errors, [k, v]) => errors.concat({ type: 'validate', name: k, message: v }),
              []
            );
            // eslint-disable-next-line no-shadow
            formattedErrors.forEach(({ name, type, message }) => setError(name, { type, message }));
          }
        },
        resetForm: reset,
      });
    });
